import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Card,
  Badge,
  Button,
  Divider,
} from '@fluentui/react-components';
import {
  NoteRegular,
  RocketRegular,
  NewRegular,
  WrenchRegular,
  BugRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { patchReleases, type PatchChangeType } from './patchNotesData';
import { usePageStyles } from '../components/layout/pageStyles';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 260px) minmax(0, 1fr)',
    gap: tokens.spacingHorizontalL,
    alignItems: 'start',
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  releaseList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    // Long history — keep the reading pane in view on tall screens.
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  releaseButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    textAlign: 'left',
    width: '100%',
    cursor: 'pointer',
    ...shorthands.border('1px', 'solid', 'transparent'),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    fontFamily: tokens.fontFamilyBase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  releaseButtonSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorBrandStroke2),
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  releaseBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginBottom: '2px',
  },
  releaseName: {
    fontSize: tokens.fontSizeBase300,
  },
  releaseNameSelected: {
    fontWeight: tokens.fontWeightSemibold,
  },
  releaseDate: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  detailCard: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  detailHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  detailMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  changeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  changeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
  },
  changeFeature: {
    borderLeft: `3px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  changeImprovement: {
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
  },
  changeFix: {
    borderLeft: `3px solid ${tokens.colorPaletteDarkOrangeBorder1}`,
  },
  changeBadge: {
    flexShrink: 0,
    marginTop: '2px',
  },
  changeText: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase400,
  },
  footerNav: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
});

type BadgeColor = 'success' | 'brand' | 'warning';

const changeBadgeColor: Record<PatchChangeType, BadgeColor> = {
  feature: 'success',
  improvement: 'brand',
  fix: 'warning',
};

function changeIcon(type: PatchChangeType) {
  if (type === 'feature') return <NewRegular />;
  if (type === 'improvement') return <WrenchRegular />;
  return <BugRegular />;
}

export default function PatchNotesPage() {
  const styles = useStyles();
  const frame = usePageStyles();
  const { t, i18n } = useTranslation();

  // Index into patchReleases (newest first), so 0 is always the current release.
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = patchReleases[selectedIndex];

  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const formatDate = useMemo(() => {
    // Release dates are date-only (YYYY-MM-DD). Parsed as UTC midnight, so format
    // in UTC too or a negative-offset browser renders the previous day.
    const fmt = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
    return (iso: string) => fmt.format(new Date(`${iso}T00:00:00Z`));
  }, [locale]);

  const changeRowClass: Record<PatchChangeType, string> = {
    feature: styles.changeFeature,
    improvement: styles.changeImprovement,
    fix: styles.changeFix,
  };

  return (
    <div className={frame.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <NoteRegular />
          <Text as="h1" className={frame.title}>
            {t('patchNotesPage.title')}
          </Text>
          <Badge appearance="tint" color="informative">
            {t('patchNotesPage.releaseCount', { count: patchReleases.length })}
          </Badge>
        </div>
        <span className={frame.subtitle}>{t('patchNotesPage.subtitle')}</span>
      </div>

      <div className={styles.layout}>
        <nav className={styles.releaseList} aria-label={t('patchNotesPage.releaseListLabel')}>
          {patchReleases.map((release, index) => {
            const isSelected = index === selectedIndex;
            return (
              <button
                key={release.version}
                type="button"
                aria-current={isSelected ? 'true' : undefined}
                className={`${styles.releaseButton} ${isSelected ? styles.releaseButtonSelected : ''}`}
                onClick={() => setSelectedIndex(index)}
              >
                <span className={styles.releaseBadges}>
                  <Badge appearance="tint" color={index === 0 ? 'success' : 'informative'} size="small">
                    {/* i18n-ignore: "v" is the version-number prefix (v1.0.0), the same in every language */}
                    v{release.version}
                  </Badge>
                  {index === 0 && (
                    <Badge appearance="outline" color="success" size="small">
                      {t('patchNotesPage.latest')}
                    </Badge>
                  )}
                </span>
                <span
                  className={`${styles.releaseName} ${isSelected ? styles.releaseNameSelected : ''}`}
                >
                  {t(`patchNotesPage.releases.${release.key}.title`)}
                </span>
                <span className={styles.releaseDate}>{formatDate(release.date)}</span>
              </button>
            );
          })}
        </nav>

        <Card className={styles.detailCard}>
          <div>
            <div className={styles.detailHeading}>
              <RocketRegular />
              <Text as="h2">{t(`patchNotesPage.releases.${selected.key}.title`)}</Text>
            </div>
            <div className={styles.detailMeta}>
              <Badge appearance="tint" color="brand">
                {/* i18n-ignore: "v" is the version-number prefix (v1.0.0), the same in every language */}
                v{selected.version}
              </Badge>
              <span>{formatDate(selected.date)}</span>
            </div>
          </div>

          <Divider />

          <div className={styles.changeList}>
            {selected.changes.map((change) => (
              <div
                key={change.key}
                className={`${styles.changeRow} ${changeRowClass[change.type]}`}
              >
                <Badge
                  className={styles.changeBadge}
                  appearance="tint"
                  color={changeBadgeColor[change.type]}
                  size="medium"
                  icon={changeIcon(change.type)}
                >
                  {t(`patchNotesPage.types.${change.type}`)}
                </Badge>
                <span className={styles.changeText}>
                  {t(`patchNotesPage.releases.${selected.key}.changes.${change.key}`)}
                </span>
              </div>
            ))}
          </div>

          <Divider />

          <div className={styles.footerNav}>
            <Button
              appearance="subtle"
              icon={<ChevronLeftRegular />}
              disabled={selectedIndex >= patchReleases.length - 1}
              onClick={() => setSelectedIndex((i) => Math.min(i + 1, patchReleases.length - 1))}
            >
              {t('patchNotesPage.older')}
            </Button>
            <Button
              appearance="subtle"
              icon={<ChevronRightRegular />}
              iconPosition="after"
              disabled={selectedIndex <= 0}
              onClick={() => setSelectedIndex((i) => Math.max(i - 1, 0))}
            >
              {t('patchNotesPage.newer')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
